import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { customerAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import Button from "../../common/Button";
import { RegistryHeader } from "../../common/Shared";

const Customer = ({ isDieFormPopup = false, onCustomerAdded }) => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    Customer_Id: 0,
    Name: "",
    Contact_No: "",
    Gst_No: "",
    Address: "",
    created_By: Number(userId),
  });

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCustomerData, setIsCustomerData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchCustomerData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await customerAPI.getAllCustomer(currentPage, pageSize);
      const newItems = res.data;

      if (currentPage === 1) {
        setIsCustomerData(newItems);
      } else {
        setIsCustomerData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching customer data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchCustomerData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isCustomerData);
    } else {
      const filtered = isCustomerData.filter((d) =>
        d.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, isCustomerData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      Customer_Id: 0,
      Name: "",
      Contact_No: "",
      Gst_No: "",
      Address: "",
      created_By: Number(userId),
    });
  };

  const handleEdit = async (id) => {
    try {
      const data = await customerAPI.getById(id);
      if (data.data) {
        setFormData({
          Customer_Id: data.data.customer_Id,
          Name: data.data.name,
          Contact_No: data.data.contact_No,
          Gst_No: data.data.gst_No,
          Address: data.data.address,
          Modify_By: Number(userId),
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch customer details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await customerAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchCustomerData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleFetchGST = async () => {
    if (!formData.Gst_No) return notify("Enter GST Number", "error");

    try {
      const res = await customerAPI.fetchGstNo(formData.Gst_No);

      const data = res?.data;

      if (!data) {
        notify("Invalid GST / No Data Found", "error");
        return;
      }

      const name = data.trade_name || data.legal_name || formData.Name;
      const addrObj = data?.place_of_business_principal?.address;

      let fullAddress = "";
      if (addrObj) {
        fullAddress = [
          addrObj?.door_num,
          addrObj?.building_name,
          addrObj?.street,
          addrObj?.location,
          addrObj?.state,
          addrObj?.pin_code,
        ]
          .filter(Boolean)
          .join(", ");
      }

      setFormData((prev) => ({
        ...prev,
        Name: name,
        Address: fullAddress || prev.Address,
      }));

      notify("GST Details Fetched!", "success");
    } catch (err) {
      notify("Invalid GST / Failed Fetch", "error");
    }
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    setIsSaving(true);
    try {
      const res = await customerAPI.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      if (isDieFormPopup && onCustomerAdded) {
        onCustomerAdded({
          value: res.data.customer_Id,
          label: res.data.name,
        });
        setIsModelOpen(false);
        return;
      }
      fetchCustomerData();
      setFormData({
        Customer_Id: 0,
        Name: "",
        Contact_No: "",
        Gst_No: "",
        Address: "",
        created_By: Number(userId),
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const fields = [
    {
      name: "Gst_No",
      label: "Gst No",
      type: "text",
      fetchable: true,
      onFetch: handleFetchGST,
    },
    {
      name: "Name",
      label: "Name",
      type: "text",
      required: true,
      errorMessage: "Name is required",
    },
    {
      name: "Contact_No",
      label: "Contact No",
      type: "text",
    },

    {
      name: "Address",
      label: "Address",
      type: "text",
      required: true,
    },
  ];

  return (
    <>
    <div className="flex flex-col h-full bg-white font-plus">
      <RegistryHeader title="Customer Master">
        <div className="relative w-full sm:w-80 group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
            <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
          </div>
          <input
            type="text"
            placeholder="Search by CUSTOMER NAME..."
            className="block w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
            >
              <MdClose size={18} />
            </button>
          )}
        </div>

        <button
          onClick={handleAdd}
          className="bg-[#3F80F7] text-white font-black text-[10px] uppercase tracking-[3px] px-8 py-3 border border-[#3F80F7] hover:bg-[#2c5ebc] transition-all flex items-center gap-3"
        >
          <HiOutlinePlus size={16} /> Add CUSTOMER
        </button>
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >
          <div className="min-w-max">
            {loading && <Loader centered />}
            <Table
              columns={[
                { key: "name", label: "Customer Name" },
                { key: "contact_No", label: "Contact No" },
                { key: "gst_No", label: "GST No" },
                { key: "address", label: "Address" },
              ]}
              data={filteredData}
              onEdit={(row) => handleEdit(row.customer_Id)}
              onDelete={(row) => handleDelete(row.customer_Id)}
              tableClassName="text-[12px] font-bold"
            />
          </div>
        </div>
      </div>
    </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Customer" : "Add Customer"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
          isSaving={isSaving}
        />
      </Model>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default Customer;
